import React, { Component } from "react"
import { SelectionManager, SharedContext } from "@albelli/photoselector"
import { ImageListView } from "./ImageListView"
import { makeImagePreviewOverlay, makeTestOverlay, makeSelectionOverlay } from "./Overlays"
import { Layout, ColumnLayout, FlexLayout, SquareLayout } from "@albelli/image-list-view"
import { UploadProgress, UploadProgressStore } from "@albelli/image-list-view-upload-progress"

import placeholder from "./Graphic/placeholder.jpg"
import StatsPanel from "./StatsPanel"
import UploadForm from "./UploadForm"
import UploadError from "./UploadError"

type ImmediateProps = {
  selection: SelectionManager
  uploader: any
  uploadStore: UploadProgressStore
  uploadProgress: UploadProgress
}

class Immediate extends Component<ImmediateProps, any> {
  static contextType = SharedContext
  private layouts = {
    square: (size: number) => new SquareLayout(size, 5, 5),
    flex: (size: number) => new FlexLayout(size, 5, 5),
    "single-column": () => new ColumnLayout(1, 5),
    "two-columns": () => new ColumnLayout(2, 5)
  }
  private defaultThumbHeight = 220
  private defaultThumbWidth = 220
  private overlays: any[]

  constructor(props: ImmediateProps) {
    super(props)
    this.state = {
      items: props.uploadStore.getFileList(),
      limit: 100,
      uploading: false,
      disableUpload: false
    }
    this.updateItemList = this.updateItemList.bind(this)
    this.checkItem = this.checkItem.bind(this)
    this.updateStats = this.updateStats.bind(this)
    this.updateThumbnail = this.updateThumbnail.bind(this)

    this.overlays = [
      this.props.uploadProgress.getOverlay(),
      makeImagePreviewOverlay(() => this.state.items.slice(0, this.state.limit).map(this.mapItem.bind(this)), this.props.selection),
      // TODO: need to update using new selector
      makeSelectionOverlay(this.props.selection, () => this.state.items.map(this.mapItem.bind(this))),
      makeTestOverlay()
    ]
  }

  trackScrolling() {
    let scrolling = false

    return (ev: any) => {
      if (scrolling) return

      const elem: HTMLDivElement = ev.target
      if (elem.scrollTop + 1000 >= elem.scrollHeight - elem.offsetHeight) {
        if (this.state.items.length > this.state.limit) {
          scrolling = true
          this.setState({ limit: this.state.limit + 100 })

          setTimeout(() => {
            scrolling = false
          }, 500)
        }
      }
    }
  }

  componentDidMount() {
    const { uploadProgress } = this.props
    if (uploadProgress) {
      uploadProgress.subscribe("fileAdded", this.updateItemList)
      uploadProgress.subscribe("thumbnail", this.updateThumbnail)
      uploadProgress.subscribe("dimensions", this.updateItemList)
      uploadProgress.subscribe("complete", this.checkItem)
      uploadProgress.subscribe("stats", this.updateStats)
      uploadProgress.subscribe("storageCoreData", this.checkItem)
    }
  }

  componentWillUnmount() {
    const { uploadProgress } = this.props
    if (uploadProgress) {
      uploadProgress.unsubscribe("fileAdded", this.updateItemList)
      uploadProgress.unsubscribe("thumbnail", this.updateThumbnail)
      uploadProgress.unsubscribe("dimensions", this.updateItemList)
      uploadProgress.unsubscribe("complete", this.checkItem)
      uploadProgress.unsubscribe("stats", this.updateStats)
      uploadProgress.unsubscribe("storageCoreData", this.checkItem)
    }
  }

  updateThumbnail() {
    this.updateItemList()
  }

  updateStats(stats: any) {
    const uploading = stats.uploaded !== stats.total - stats.failed
    const { completeOnSelect } = this.context
    if (uploading != this.state.uploading) {
      const disableUpload = completeOnSelect && uploading
      this.setState({ uploading: uploading, disableUpload: disableUpload })
    }
  }

  filesAdded(fileList: File[]) {
    this.props.uploader.addFiles(fileList)
  }

  updateItemList() {
    const items = this.props.uploadStore.getFileList()

    //Update state
    this.setState({ items: items })
  }

  checkItem(data: any) {
    const items = this.state.items.filter((it: any) => it.localId == data.localId)
    if (items.length > 0) {
      const mappedItem = this.mapItem(items[0])
      // TODO: this should emit adding items in new selection store
      this.props.selection.select(mappedItem)
      this.updateItemList()
    }
  }

  mapItem(data: any) {
    const { uploadProgress } = this.props
    const thumbnail = uploadProgress.getThumbnailData(data.localId, 300) as any
    let urlPreview = null
    if (data.hasOwnProperty("state") && data.state == "complete") {
      const previewData = uploadProgress.getThumbnailData(data.localId, 1300) as any
      if (previewData) {
        urlPreview = previewData.url
      }
    }
    let width = this.defaultThumbWidth
    let height = this.defaultThumbHeight
    if (thumbnail != null) {
      width = thumbnail.thumbWidth
      height = thumbnail.thumbHeight
    } else if (data.width > 0 && data.height > 0) {
      if (data.width > data.height) {
        width = this.defaultThumbWidth
        height = (data.height / data.width) * this.defaultThumbHeight
      } else {
        height = this.defaultThumbHeight
        width = (data.width / data.height) * this.defaultThumbWidth
      }
    }
    const url = thumbnail != null ? thumbnail.url : placeholder
    // online uploader does not return some of the meta, so it is generating on the spot
    return {
      id: data.localId || data.regId,
      url: url,
      name: "",
      dateTaken: new Date(),
      urlPreview: urlPreview || url,
      urlThumbnail: url,
      source: "My Device-OP",
      regId: data.hasOwnProperty("regId") ? data.regId : null,
      size: {
        width: width,
        height: height
      }
    }
  }

  render() {
    const layout: Layout = this.layouts["flex"](this.defaultThumbHeight)
    const items = this.state.items.slice(0, this.state.limit).map(this.mapItem.bind(this))
    const { completeOnSelect } = this.context
    return (
      <div className={`photoselector-mydevice-container ${completeOnSelect ? "no-SelectionPreview" : ""}`}>
        {!this.state.disableUpload && (
          <UploadForm expanded={items.length == 0} addFiles={this.filesAdded.bind(this)} maxFileSize={this.props.uploader.maxFileSize} />
        )}
        {items.length > 1 && <StatsPanel uploadProgress={this.props.uploadProgress} uploadStore={this.props.uploadStore} />}
        {items.length > 0 && (
          <div className={"photoselector-mydevice-imagelist" + (this.state.uploading ? " uploading" : "")}>
            <ImageListView
              className={"mydevice-image-container"}
              layout={layout}
              items={items}
              overlays={this.overlays}
              onScroll={this.trackScrolling()}
              data-tam="upload-images"
            />
            <UploadError
              uploadProgress={this.props.uploadProgress}
              uploadStore={this.props.uploadStore}
              maxFileSize={this.props.uploader.maxFileSize}
              data-tam="upload-image"
            />
          </div>
        )}
      </div>
    )
  }
}

export default Immediate
